import { zodResolver } from '@hookform/resolvers/zod';
import {
	Button,
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
	FormValidationDialog,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
	TextNumber,
	createFormLabels
} from '@nest/uikit-react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { z } from 'zod';
import { useTerminalClient } from '../client-rpc-router';
import { StockItemLinkDetail } from '../components/StockItemLinkDetail';
import { StockItemScanInput } from '../components/StockItemScanInput';
import { StorageLocationScanInput } from '../components/StorageLocationScanInput';
import { TerminalScreen } from '../components/TerminalScreen';
import { useNumberParam } from '../helpers';

export default function DeliveryStopItemProcessPage() {
	const shipmentRouteId = useNumberParam('shipmentRouteId');
	const warehouseRelationId = useNumberParam('warehouseRelationId');
	const shipmentStopId = useNumberParam('shipmentStopId');
	const stockItemId = useNumberParam('stockItemId');
	const client = useTerminalClient();
	const navigate = useNavigate();

	const [activeTab, setActiveTab] = useState<'sourceLocation' | 'stockItem' | 'quantity'>('sourceLocation');

	const formSchema = useMemo(
		() =>
			z.object({
				sourceLocation: z.any().nullable(),
				stockItem: z.any().nullable(),
				quantity: z.number().min(0, 'Množství musí být větší než 0')
			}),
		[]
	);

	type FormData = z.infer<typeof formSchema>;

	const form = useForm<FormData, any, FormData>({
		resolver: zodResolver(formSchema),
		defaultValues: {
			sourceLocation: null,
			stockItem: null
		},
		mode: 'onSubmit'
	});

	const { watch } = form;
	const watchedValues = watch();

	const { data: oneItem } = useQuery({
		queryKey: ['shipment-route-one-item-to-load', shipmentRouteId, shipmentStopId, stockItemId],
		queryFn: () =>
			client.shipments.getShipmentRouteOneItemToLoadQuery.execute({
				shipmentRouteId: shipmentRouteId,
				shipmentStopId: shipmentStopId,
				stockItemId: stockItemId
			}),
		enabled: Boolean(shipmentRouteId) && Boolean(shipmentStopId) && Boolean(stockItemId)
	});

	useEffect(() => {
		if (watchedValues.sourceLocation) {
			setActiveTab('stockItem');
		}
	}, [watchedValues.sourceLocation]);

	useEffect(() => {
		if (watchedValues.stockItem) {
			setActiveTab('quantity');
		}
	}, [watchedValues.stockItem]);

	const confirmMutation = useMutation({
		mutationFn: async (data: FormData) => {
			await client.shipments.confirmShipmentItemToLoadMutation.execute({
				shipmentRouteId: shipmentRouteId,
				shipmentStopId: shipmentStopId,
				stockItem: data.stockItem,
				sourceLocation: data.sourceLocation,
				quantity: data.quantity as number
			});
		},
		onSuccess: () => navigate(`/delivery/list/${shipmentRouteId}/process/${warehouseRelationId}`)
	});

	const suggestedLocationName = oneItem?.suggestedStockItemLocation?.storageLocation?.name;
	const suggestedStockItemId = oneItem?.stockItem?.id;

	const sourceLocationClasses = useMemo(() => {
		if (watchedValues.sourceLocation == null) {
			return 'bg-transparent';
		}
		if (watchedValues.sourceLocation?.id === oneItem?.suggestedStockItemLocation?.storageLocation?.id) {
			return 'bg-green-200 data-[state=active]:bg-green-300';
		}
		return 'bg-red-200 data-[state=active]:bg-red-300';
	}, [watchedValues.sourceLocation, oneItem?.suggestedStockItemLocation?.storageLocation?.id]);

	const stockItemClasses = useMemo(() => {
		if (watchedValues.stockItem == null) {
			return 'bg-transparent';
		}
		if (watchedValues.stockItem?.id === suggestedStockItemId) {
			return 'bg-green-200 data-[state=active]:bg-green-300';
		}
		return 'bg-red-200 data-[state=active]:bg-red-300';
	}, [watchedValues.stockItem, suggestedStockItemId]);

	const formLabels = createFormLabels<any>(form.control, {
		stockItem: 'Položka',
		sourceLocation: 'Lokace',
		quantity: 'Množství'
	});

	const handleSubmit = form.handleSubmit((data: FormData) => {
		confirmMutation.mutateAsync(data);
	});

	const bottomContent = oneItem ? (
		<Button variant="default" onClick={handleSubmit} disabled={confirmMutation.isPending} className="w-full">
			Potvrdit položku
		</Button>
	) : undefined;

	return (
		<TerminalScreen showBackButton topBar={<StockItemLinkDetail stockItemId={stockItemId} />} bottomContent={bottomContent}>
			<div className="h-full flex flex-col">
				{oneItem ? (
					<>
						<Table>
							<TableBody>
								<TableRow>
									<TableHead className="w-1/2">Návrh lokace</TableHead>
									<TableCell className="w-1/2">{suggestedLocationName}</TableCell>
								</TableRow>
								<TableRow>
									<TableHead className="w-1/2">Zbývá</TableHead>
									<TableCell className="w-1/2">{oneItem.quantityToLoad}</TableCell>
								</TableRow>
							</TableBody>
						</Table>

						<div className="flex-1 overflow-auto p-4">
							<Form {...form}>
								<FormValidationDialog errors={[confirmMutation.error]} control={form.control} labels={formLabels} />
								<div className="h-full flex flex-col">
									<Tabs value={activeTab} onValueChange={v => setActiveTab(v as any)} className="w-full">
										<TabsList className="grid grid-cols-3 w-full">
											<TabsTrigger value="sourceLocation" className={sourceLocationClasses}>
												Lokace
											</TabsTrigger>
											<TabsTrigger value="stockItem" className={stockItemClasses}>
												SKU
											</TabsTrigger>
											<TabsTrigger value="quantity">Množství</TabsTrigger>
										</TabsList>

										<TabsContent value="sourceLocation" className="space-y-4 p-2">
											<FormField
												control={form.control}
												name="sourceLocation"
												render={({ field }) => (
													<FormItem>
														<FormLabel className="sr-only">Lokace</FormLabel>
														<FormControl>
															<StorageLocationScanInput value={field.value} autoFocus placeholder="Naskenujte lokaci..." onChange={field.onChange} />
														</FormControl>
														<FormMessage />
													</FormItem>
												)}
											/>
										</TabsContent>

										<TabsContent value="stockItem" className="space-y-4 p-2">
											<FormField
												control={form.control}
												name="stockItem"
												render={({ field }) => (
													<FormItem>
														<FormLabel className="sr-only">Položka</FormLabel>
														<FormControl>
															<StockItemScanInput value={field.value} autoFocus placeholder="Naskenujte položku..." onChange={field.onChange} />
														</FormControl>
														<FormMessage />
													</FormItem>
												)}
											/>
										</TabsContent>

										<TabsContent value="quantity" className="space-y-4 p-2">
											<FormField
												control={form.control}
												name="quantity"
												render={({ field, fieldState }) => (
													<FormItem>
														<FormLabel className="sr-only">Množství</FormLabel>
														<FormControl>
															<TextNumber value={field.value} onChange={field.onChange} placeholder="Množství" autoFocus={true} />
														</FormControl>
														<FormMessage />
													</FormItem>
												)}
											/>
										</TabsContent>
									</Tabs>
								</div>
							</Form>
						</div>
					</>
				) : (
					<div className="p-6 text-center text-muted-foreground">Žádná položka k naložení</div>
				)}
			</div>
		</TerminalScreen>
	);
}
